/**
 * HarnessFields: the TaskHopper adapter that runs one benchmark, with the
 * inputs its descriptor declares. The adapter is picked from the ones the
 * platform ships (`GET /api/benchmarks/adapters`), so a typed name the
 * nightly-evals image has no directory for cannot reach a save. The
 * routes come from the picked adapter's declaration (RouteFields). The
 * options and aliases are not checked against the descriptor here;
 * TaskHopper refuses an undeclared option when the job runs.
 */

import type { BenchmarkAdapterItem } from '@/api/generated/model'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import type { HarnessRow, PairRow } from './benchmarkSources'
import { PairsEditor } from './PairsEditor'
import { RouteFields } from './RouteFields'
import { TextField } from './TextField'

/** The adapters the platform ships, as the page read them. */
export interface AdapterChoices {
  adapters: BenchmarkAdapterItem[]
  pending: boolean
  /** Why the list could not be read, or null. */
  error: string | null
}

interface HarnessFieldsProps {
  /** Prefix for the inputs' ids, unique on the page. */
  id: string
  harness: HarnessRow
  adapters: AdapterChoices
  /** The eval PR layout is saved: the adapter's case file rule names the tests open eval PRs are resolved against. */
  frozenAdapter: boolean
  onChange: (harness: HarnessRow) => void
}

export function HarnessFields({
  id,
  harness,
  adapters,
  frozenAdapter,
  onChange,
}: HarnessFieldsProps): React.JSX.Element {
  const updateMap =
    (map: 'options' | 'routes' | 'aliases') =>
    (next: (previous: PairRow[]) => PairRow[]): void => {
      onChange({ ...harness, [map]: next(harness[map]) })
    }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <AdapterPicker
          id={`${id}-adapter`}
          value={harness.adapter}
          choices={adapters}
          frozen={frozenAdapter}
          onPick={(adapter) => {
            onChange({ ...harness, adapter })
          }}
        />
        <TextField
          id={`${id}-concurrency`}
          label="Concurrency"
          value={harness.concurrency}
          type="number"
          mono
          placeholder="8"
          hint="The night runs this many reference containers at once for this benchmark. Leave it empty for the night's default."
          onChange={(concurrency) => {
            onChange({ ...harness, concurrency })
          }}
        />
      </div>
      <PairsEditor
        id={`${id}-option`}
        title="Options"
        hint="The adapter declares which options it takes. A whole number is sent as a number and true or false as a boolean. Anything else is sent as text. A saved value you leave unchanged keeps its type."
        keyLabel="name"
        valueLabel="value"
        keyPlaceholder="suitePath"
        valuePlaceholder="src/agent/__tests__/benchmark"
        addLabel="Add option"
        rows={harness.options}
        onChange={updateMap('options')}
      />
      <RouteFields
        id={id}
        routes={harness.routes}
        declared={adapters.adapters.find((adapter) => adapter.name === harness.adapter)?.routes}
        onChange={updateMap('routes')}
      />
      <PairsEditor
        id={`${id}-alias`}
        title="Aliases"
        hint="Map each model name the packaged application sends to the catalog model that answers it."
        keyLabel="application model"
        valueLabel="catalog model"
        keyPlaceholder="gpt-5-mini"
        valuePlaceholder="openai/gpt-5-mini"
        addLabel="Add alias"
        rows={harness.aliases}
        onChange={updateMap('aliases')}
      />
    </div>
  )
}

const UNSHIPPED_ADAPTER = 'The platform does not ship this adapter.'

/**
 * A select over the adapters the platform ships, each with what it runs.
 * A stored name the list lacks (an adapter since retired, or a name typed
 * before this was a select) keeps an entry, so the row still loads and
 * reads what the night would run. The api accepts a name the row already
 * stores, so the rest of the row stays editable.
 */
function AdapterPicker({
  id,
  value,
  choices,
  frozen,
  onPick,
}: {
  id: string
  value: string
  choices: AdapterChoices
  frozen: boolean
  onPick: (adapter: string) => void
}): React.JSX.Element {
  const listed = choices.adapters.find((adapter) => adapter.name === value)
  const options: BenchmarkAdapterItem[] =
    listed === undefined && value !== ''
      ? [{ name: value, description: UNSHIPPED_ADAPTER, routes: [] }, ...choices.adapters]
      : choices.adapters
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>TaskHopper adapter</Label>
      <Select
        value={value}
        disabled={frozen || choices.pending || options.length === 0}
        onValueChange={onPick}
      >
        <SelectTrigger id={id} className="font-mono" aria-describedby={`${id}-hint`}>
          {/* The trigger shows the name alone; the description is in the hint. */}
          <SelectValue placeholder="No adapter picked">{value}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((adapter) => (
            <SelectItem key={adapter.name} value={adapter.name}>
              <span className="flex flex-col gap-0.5">
                <span className="font-mono">{adapter.name}</span>
                <span className="text-xs text-muted-foreground">{adapter.description}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        <AdapterHint value={value} listed={listed} choices={choices} frozen={frozen} />
      </p>
    </div>
  )
}

function AdapterHint({
  value,
  listed,
  choices,
  frozen,
}: {
  value: string
  listed: BenchmarkAdapterItem | undefined
  choices: AdapterChoices
  frozen: boolean
}): React.JSX.Element {
  if (frozen) {
    return (
      <>
        It was set with the eval PR layout. Open eval PRs are resolved against the test files its
        case file rule names.
      </>
    )
  }
  if (choices.pending) return <>The adapter list is being read.</>
  if (choices.error !== null) return <>{choices.error}</>
  if (listed !== undefined) return <>{listed.description}</>
  if (value !== '') {
    return <>{UNSHIPPED_ADAPTER} The night cannot run it, so pick one from the list.</>
  }
  return <>Pick the adapter that runs this benchmark&apos;s tests.</>
}
